import type {
  DefaultLineItemRequest,
  DefaultLineItemResponse,
  InvoiceLineItemType,
  InvoiceType,
} from "components/Invoicing/InvoiceTypes.ts";

import { api } from "./api.ts";

type GetInvoicesArgs = {
  entityId?: string | number;
  entityType?: string;
  service?: string | number;
  file?: string | number;
  client?: string | number;
  is_cms_invoice?: boolean;
  invoiceType?:
    | "file_service"
    | "section_111"
    | "future_medical"
    | "lien"
    | "document_management";
};

type GetInvoiceByIdArgs = {
  id: string | number;
};

type PatchInvoiceArgs = {
  id: string | number;
  data: Partial<InvoiceType>;
};

type CreateInvoiceArgs = {
  data: Partial<InvoiceType>;
};

type DeleteInvoiceArgs = {
  id: string | number;
  delete_batch?: boolean;
};

type CreateInvoiceLineItemArgs = {
  data: Partial<InvoiceLineItemType>;
};

type GetInvoiceLineItemArgs = {
  id: number;
};

type UpdateInvoiceLineItemArgs = {
  id: number;
  data: Partial<InvoiceLineItemType>;
};

type DeleteInvoiceLineItemArgs = {
  id: number;
  invoiceId: number;
};

type GenerateDefaultLineItemArgs = {
  invoice_id: number;
  pricing_field: string;
};

type FinalizeInvoiceArgs = {
  id: string | number;
  data?: Partial<InvoiceType>;
};

type VoidInvoiceArgs = {
  id: string | number;
};

type VoidAndReissueInvoiceArgs = {
  id: string | number;
  reissue?: boolean;
};

type BatchPostInvoicesArgs = {
  invoice_ids: number[];
  post_entire_bulk?: boolean;
};

type MarkAsManuallySentArgs = {
  id: string | number;
};

type UnmarkAsManuallySentArgs = {
  id: string | number;
};

export const invoiceAPI = api.injectEndpoints({
  endpoints: (build) => ({
    getInvoices: build.query<InvoiceType[], GetInvoicesArgs>({
      query: ({ file, service, client, is_cms_invoice, invoiceType }) => {
        const params = new URLSearchParams();
        if (file) params.append("file_id", String(file));
        if (service) params.append("service_id", String(service));
        if (client) params.append("client_id", String(client));
        if (is_cms_invoice !== undefined)
          params.append("is_cms_invoice", String(is_cms_invoice));
        if (invoiceType) params.append("invoice_type", invoiceType);

        return {
          url: `api/v1/invoices/?${params.toString()}`,
          method: "GET",
        };
      },
      transformResponse: (response: { results: InvoiceType[] }) =>
        response.results,
      providesTags: ["Invoices"],
    }),
    patchInvoiceById: build.mutation<InvoiceType, PatchInvoiceArgs>({
      query: ({ id, data }) => ({
        url: `api/v1/invoices/${id}/`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: ["Invoices"],
    }),
    getInvoiceById: build.query<InvoiceType, GetInvoiceByIdArgs>({
      query: ({ id }) => ({
        url: `api/v1/invoices/${id}/`,
        method: "GET",
      }),
      providesTags: (result) =>
        result ? [{ type: "Invoice", id: result.id }] : [],
    }),
    createInvoice: build.mutation<InvoiceType, CreateInvoiceArgs>({
      query: ({ data }) => ({
        url: `api/v1/invoices/`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Invoices"],
    }),
    deleteInvoiceById: build.mutation<InvoiceType, DeleteInvoiceArgs>({
      query: ({ id, delete_batch }) => ({
        url: `api/v1/invoices/${id}/${delete_batch ? "?delete_batch=true" : ""}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Invoices"],
    }),
    createInvoiceLineItem: build.mutation<
      InvoiceLineItemType,
      CreateInvoiceLineItemArgs
    >({
      query: ({ data }) => ({
        url: `api/v1/invoice-line-items/`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: (result) => [
        { type: "InvoiceLineItem", id: result?.id },
        { type: "Invoice", id: result?.invoice },
      ],
    }),
    getInvoiceLineItem: build.query<
      InvoiceLineItemType,
      GetInvoiceLineItemArgs
    >({
      query: ({ id }) => ({
        url: `api/v1/invoice-line-items/${id}/`,
        method: "GET",
      }),
      providesTags: (result) =>
        result ? [{ type: "InvoiceLineItem", id: result.id }] : [],
    }),
    updateInvoiceLineItem: build.mutation<
      InvoiceLineItemType,
      UpdateInvoiceLineItemArgs
    >({
      query: ({ id, data }) => ({
        url: `api/v1/invoice-line-items/${id}/`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: (result) => {
        return [
          { type: "InvoiceLineItem", id: result?.id },
          { type: "Invoice", id: result?.invoice },
        ];
      },
    }),
    deleteInvoiceLineItem: build.mutation<void, DeleteInvoiceLineItemArgs>({
      query: ({ id }) => ({
        url: `api/v1/invoice-line-items/${id}/`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, { invoiceId }) => [
        { type: "InvoiceLineItem", id: invoiceId },
        { type: "Invoice", id: invoiceId },
      ],
    }),
    generateDefaultLineItem: build.mutation<
      DefaultLineItemResponse,
      GenerateDefaultLineItemArgs
    >({
      query: ({ invoice_id, pricing_field }) => ({
        url: `api/v1/invoice-line-items/generate-default-line-item/`,
        method: "POST",
        body: { invoice_id, pricing_field } as DefaultLineItemRequest,
      }),
      invalidatesTags: (result) => [
        { type: "InvoiceLineItem", id: result?.id },
        { type: "Invoice", id: result?.invoice },
      ],
    }),
    finalizeInvoice: build.mutation<InvoiceType, FinalizeInvoiceArgs>({
      query: ({ id, data }) => ({
        url: `api/v1/invoices/${id}/finalize/`,
        method: "POST",
        body: data || {},
      }),
      invalidatesTags: (result) =>
        result
          ? [
              { type: "Invoice", id: result.id },
              { type: "Invoices" },
              { type: "Documents" },
            ]
          : [],
    }),
    voidInvoice: build.mutation<void, VoidInvoiceArgs>({
      query: ({ id }) => ({
        url: `api/v1/invoices/${id}/void/`,
        method: "POST",
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "Invoice", id },
        { type: "Invoices" },
      ],
    }),
    voidAndReissueInvoice: build.mutation<
      {
        voided_invoice: InvoiceType;
        reissued_invoice?: InvoiceType;
        message: string;
      },
      VoidAndReissueInvoiceArgs
    >({
      query: ({ id, reissue = true }) => ({
        url: `api/v1/invoices/${id}/void_and_reissue/`,
        method: "POST",
        body: { reissue },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "Invoice", id },
        { type: "Invoices" },
      ],
    }),
    batchPostInvoices: build.mutation<
      {
        task_id: string;
        task_status: string;
      },
      BatchPostInvoicesArgs
    >({
      query: ({ invoice_ids, post_entire_bulk }) => ({
        url: `api/v1/invoices/batch_post/`,
        method: "POST",
        body: { invoice_ids, ...(post_entire_bulk && { post_entire_bulk }) },
      }),
      invalidatesTags: ["Invoices"],
    }),
    getBatchSummary: build.query<
      {
        invoice_count: number;
        total_amount: number;
        client_name: string;
        claim_count: number;
        earliest_service_date: string | null;
        latest_service_date: string | null;
      },
      string // bulk_id
    >({
      query: (bulkId) => ({
        url: `api/v1/invoices/batch_summary/?bulk_id=${bulkId}`,
      }),
    }),
    batchVoidInvoices: build.mutation<
      { task_id?: string; task_status?: string; message?: string },
      { invoice_ids: number[]; void_entire_bulk?: boolean }
    >({
      query: ({ invoice_ids, void_entire_bulk }) => ({
        url: `api/v1/invoices/batch_void/`,
        method: "POST",
        body: {
          invoice_ids,
          ...(void_entire_bulk && { void_entire_bulk }),
        },
      }),
      invalidatesTags: ["Invoices"],
    }),
    getBatchVoidSummary: build.query<
      {
        invoice_count: number;
        total_amount: number;
        client_name: string;
        earliest_service_date: string | null;
        latest_service_date: string | null;
      },
      string // bulk_id
    >({
      query: (bulkId) => ({
        url: `api/v1/invoices/batch_void_summary/?bulk_id=${bulkId}`,
      }),
    }),
    markAsManuallySent: build.mutation<
      {
        message: string;
        invoice: InvoiceType;
      },
      MarkAsManuallySentArgs
    >({
      query: ({ id }) => ({
        url: `api/v1/invoices/${id}/mark_as_manually_sent/`,
        method: "POST",
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "Invoice", id },
        { type: "Invoices" },
      ],
    }),
    unmarkAsManuallySent: build.mutation<
      {
        message: string;
        invoice: InvoiceType;
      },
      UnmarkAsManuallySentArgs
    >({
      query: ({ id }) => ({
        url: `api/v1/invoices/${id}/unmark_as_manually_sent/`,
        method: "POST",
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "Invoice", id },
        { type: "Invoices" },
      ],
    }),
    createSection111Invoice: build.mutation<
      {
        created_invoices: {
          id: number;
          status: string;
          total: string;
          line_items_count: number;
        }[];
        total_invoices_created: number;
      },
      {
        rre_id: number;
        file_type: string;
        detail_ids?: number[];
        select_all_filters?: {
          expected_count: number;
          date_from?: string;
          date_to?: string;
          hide_invoiced?: boolean;
          nsa_filter?: string;
          action_filter?: string;
          doi_from?: string;
          doi_to?: string;
          tpoc_from?: string;
          tpoc_to?: string;
          medicare_from?: string;
          medicare_to?: string;
        };
        client_id: number;
        quickbooks_workspace_id: number;
        assignee_id: number;
        fee_config: Record<string, unknown>;
        batching_mode: string;
        email_subject: string;
        description?: string;
        terms?: string;
        billing_email_address?: string;
        force_override?: boolean;
      }
    >({
      query: (body) => ({
        url: `api/v1/section111-invoicing/create/`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["Invoices"],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetInvoicesQuery,
  usePatchInvoiceByIdMutation,
  useCreateInvoiceMutation,
  useDeleteInvoiceByIdMutation,
  useGetInvoiceByIdQuery,
  useCreateInvoiceLineItemMutation,
  useGetInvoiceLineItemQuery,
  useUpdateInvoiceLineItemMutation,
  useDeleteInvoiceLineItemMutation,
  useGenerateDefaultLineItemMutation,
  useFinalizeInvoiceMutation,
  useVoidInvoiceMutation,
  useVoidAndReissueInvoiceMutation,
  useBatchPostInvoicesMutation,
  useBatchVoidInvoicesMutation,
  useLazyGetBatchSummaryQuery,
  useLazyGetBatchVoidSummaryQuery,
  useMarkAsManuallySentMutation,
  useUnmarkAsManuallySentMutation,
  useCreateSection111InvoiceMutation,
} = invoiceAPI;
